/**
 * Arena Explorer - Professional Interactive Seat Map
 * Elvis Aloha Collection
 * 
 * A museum-quality interactive arena seating chart with:
 * - Progressive zoom: Arena → Section → Seat
 * - Clear historical uncertainty labeling
 * - Stage-affected areas visible but marked
 * - Section L preserved under stage overlay
 * - Professional hover states and modals
 * - Mobile pinch-zoom and pan support
 * - Quick edit panel for authenticated owners
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import QuickEditPanel from './QuickEditPanel';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// ============================================================================
// CONSTANTS
// ============================================================================

const COLORS = {
  // Arena structure
  arenaOutline: '#d4d4d4',
  upperLevel: '#f5f5f5',
  arenaLevel: '#fafafa',
  logeLevel: '#f0f0f0',
  floorArea: '#ffffff',
  
  // Stage and blocked areas
  stageArea: '#2a2a2a',
  stageAffected: 'rgba(212, 131, 122, 0.15)',
  stageAffectedHatch: 'url(#stage-hatch)',
  runway: '#3a3a3a',
  mirrors: '#4a4a4a',
  
  // Seats
  seatEmpty: '#e8e8e8',
  seatKnown: '#C9A962',
  seatKnownHover: '#e0c47a',
  seatPrincipalArchive: '#C9A962',
  seatExternal: '#4A9B8C',
  seatUnderReview: '#D4837A',
  seatGraceland50: '#FFD700',
  
  // Section highlights
  sectionHasTickets: 'rgba(201, 169, 98, 0.15)',
  sectionHover: 'rgba(201, 169, 98, 0.25)',
  sectionStageAffected: 'rgba(212, 131, 122, 0.1)',
};

const LAYOUT_VIEWS = [
  { id: 'aloha-1973', name: 'Aloha Concert Reconstruction', type: 'reconstruction' },
  { id: 'full-arena', name: 'Full Arena Reference', type: 'reference' },
  { id: 'venue-stage', name: 'Stage Show Reference', type: 'venue' },
  { id: 'venue-360', name: '360 Stage Reference', type: 'venue' },
  { id: 'venue-boxing', name: 'Boxing Reference', type: 'venue' },
];

const DATA_LAYERS = [
  { id: 'all', name: 'All Known Tickets' },
  { id: 'original_2021', name: 'Original 2021 Collection' },
  { id: 'principal', name: 'Principal Archive' },
  { id: 'graceland_display', name: 'Graceland Display Selection' },
  { id: 'external', name: 'Known External Tickets' },
];

// ============================================================================
// ARENA GEOMETRY - Blaisdell-style layout
// ============================================================================

const ARENA_GEOMETRY = {
  width: 1200,
  height: 900,
  centerX: 600,
  centerY: 450,
  
  // Upper level (permanent)
  upperLevel: {
    outerRadius: 420,
    innerRadius: 340,
    startAngle: -Math.PI * 0.8,
    endAngle: Math.PI * 0.8,
  },
  
  // Arena level (reconfigurable)
  arenaLevel: {
    outerRadius: 320,
    innerRadius: 200,
    startAngle: -Math.PI * 0.75,
    endAngle: Math.PI * 0.75,
  },
  
  // Floor area
  floorArea: {
    radius: 180,
  },
  
  // Stage footprint (Aloha reconstruction)
  stage: {
    width: 200,
    height: 120,
    x: 500,
    y: 380,
    runwayLength: 100,
    runwayWidth: 30,
    mirrorWidth: 60,
  }
};

// Section geometry based on actual inventory data
const SECTIONS = {
  'AA': {
    level: 'arena',
    startAngle: -2.4,
    endAngle: -2.0,
    innerRadius: 220,
    outerRadius: 300,
    labelX: 180,
    labelY: 280,
    ticketCount: 1,
    isStageAffected: false,
  },
  'BB': {
    level: 'arena',
    startAngle: -1.8,
    endAngle: -1.2,
    innerRadius: 220,
    outerRadius: 300,
    labelX: 280,
    labelY: 180,
    ticketCount: 38,
    isStageAffected: false,
  },
  'H': {
    level: 'upper',
    startAngle: -0.8,
    endAngle: 0.0,
    innerRadius: 350,
    outerRadius: 420,
    labelX: 450,
    labelY: 100,
    ticketCount: 44,
    isStageAffected: false,
  },
  'L': {
    level: 'arena',
    startAngle: 0.4,
    endAngle: 1.2,
    innerRadius: 200,
    outerRadius: 300,
    labelX: 750,
    labelY: 250,
    ticketCount: 53,
    isStageAffected: true,
  }
};

// ============================================================================
// SVG PATH GENERATORS
// ============================================================================

const generateArcPath = (cx, cy, innerR, outerR, startAngle, endAngle) => {
  const x1 = cx + Math.cos(startAngle) * innerR;
  const y1 = cy + Math.sin(startAngle) * innerR;
  const x2 = cx + Math.cos(endAngle) * innerR;
  const y2 = cy + Math.sin(endAngle) * innerR;
  const x3 = cx + Math.cos(endAngle) * outerR;
  const y3 = cy + Math.sin(endAngle) * outerR;
  const x4 = cx + Math.cos(startAngle) * outerR;
  const y4 = cy + Math.sin(startAngle) * outerR;
  
  const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;
  
  return `M ${x1} ${y1} A ${innerR} ${innerR} 0 ${largeArc} 1 ${x2} ${y2} L ${x3} ${y3} A ${outerR} ${outerR} 0 ${largeArc} 0 ${x4} ${y4} Z`;
};

const generateStagePath = () => {
  const { stage } = ARENA_GEOMETRY;
  const cx = ARENA_GEOMETRY.centerX;
  const cy = ARENA_GEOMETRY.centerY;
  
  // Main stage platform
  const mainStage = `M ${cx - stage.width/2} ${cy - stage.height/2} 
                     L ${cx + stage.width/2} ${cy - stage.height/2} 
                     L ${cx + stage.width/2} ${cy + stage.height/2} 
                     L ${cx - stage.width/2} ${cy + stage.height/2} Z`;
  
  // Runway extending toward audience
  const runway = `M ${cx - stage.runwayWidth/2} ${cy + stage.height/2} 
                  L ${cx + stage.runwayWidth/2} ${cy + stage.height/2} 
                  L ${cx + stage.runwayWidth/2} ${cy + stage.height/2 + stage.runwayLength} 
                  L ${cx - stage.runwayWidth/2} ${cy + stage.height/2 + stage.runwayLength} Z`;
  
  return { mainStage, runway };
};

// ============================================================================
// HOOKS
// ============================================================================

const useArenaSections = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSections = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/seat-map/arena/sections`);
        const data = await res.json();
        setSections(data.sections || []);
      } catch (err) {
        console.error('Failed to fetch sections:', err);
        // Use default section data
        setSections(Object.entries(SECTIONS).map(([id, s]) => ({
          id: `section-${id}`,
          name: `Section ${id}`,
          ...s
        })));
      } finally {
        setLoading(false);
      }
    };
    fetchSections();
  }, []);

  return { sections, loading };
};

const useSectionTickets = (sectionId) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!sectionId) return;
    
    const fetchSection = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE}/api/seat-map/arena/sections/${sectionId}`);
        const result = await res.json();
        setData(result);
      } catch (err) {
        console.error('Failed to fetch section:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchSection();
  }, [sectionId]);

  return { data, loading };
};

const useInventoryStats = () => {
  const [stats, setStats] = useState(null);
  
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/seat-map/inventory/stats`);
        const data = await res.json();
        setStats(data);
      } catch (err) {
        console.error('Failed to fetch stats:', err);
      }
    };
    fetchStats();
  }, []);
  
  return stats;
};

// ============================================================================
// COMPONENTS
// ============================================================================

const ArenaOverview = ({ 
  sections, 
  layoutView, 
  onSectionClick, 
  onSectionHover,
  hoveredSection 
}) => {
  const { centerX, centerY } = ARENA_GEOMETRY;
  const stagePaths = useMemo(() => generateStagePath(), []);
  const showStage = layoutView === 'aloha-1973';
  
  return (
    <g data-testid="arena-overview">
      {/* SVG Definitions */}
      <defs>
        {/* Stage-affected hatch pattern */}
        <pattern id="stage-hatch" patternUnits="userSpaceOnUse" width="8" height="8">
          <path d="M-1,1 l2,-2 M0,8 l8,-8 M7,9 l2,-2" 
                stroke="rgba(212, 131, 122, 0.3)" strokeWidth="1"/>
        </pattern>
        
        {/* Graceland display ring glow */}
        <filter id="graceland-glow">
          <feGaussianBlur stdDeviation="2" result="blur"/>
          <feMerge>
            <feMergeNode in="blur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>
      
      {/* Arena outline */}
      <ellipse
        cx={centerX}
        cy={centerY}
        rx={430}
        ry={400}
        fill="none"
        stroke={COLORS.arenaOutline}
        strokeWidth="3"
      />
      
      {/* Upper level ring */}
      <ellipse
        cx={centerX}
        cy={centerY}
        rx={420}
        ry={380}
        fill={COLORS.upperLevel}
        stroke={COLORS.arenaOutline}
        strokeWidth="1"
      />
      
      {/* Arena level ring */}
      <ellipse
        cx={centerX}
        cy={centerY}
        rx={320}
        ry={290}
        fill={COLORS.arenaLevel}
        stroke={COLORS.arenaOutline}
        strokeWidth="1"
      />
      
      {/* Floor area */}
      <ellipse
        cx={centerX}
        cy={centerY}
        rx={180}
        ry={160}
        fill={COLORS.floorArea}
        stroke={COLORS.arenaOutline}
        strokeWidth="1"
      />
      
      {/* Level labels */}
      <text x={centerX} y={60} textAnchor="middle" 
            fill="#888" fontSize="14" fontWeight="600"
            fontFamily="'Source Sans 3', sans-serif">
        UPPER LEVEL
      </text>
      <text x={centerX} y={840} textAnchor="middle" 
            fill="#888" fontSize="14" fontWeight="600"
            fontFamily="'Source Sans 3', sans-serif">
        ARENA LEVEL
      </text>
      
      {/* Sections with ticket data */}
      {sections.map(section => {
        const sectionId = section.name?.replace('Section ', '') || section.id?.replace('section-', '');
        const geo = SECTIONS[sectionId];
        if (!geo) return null;
        
        const path = generateArcPath(
          centerX, centerY,
          geo.innerRadius, geo.outerRadius,
          geo.startAngle, geo.endAngle
        );
        
        const hasTickets = section.known_ticket_count > 0;
        const isHovered = hoveredSection === sectionId;
        const isStageAffected = section.is_stage_affected;
        
        return (
          <g 
            key={sectionId} 
            data-testid={`section-${sectionId}`}
            onClick={() => onSectionClick(sectionId)}
            onMouseEnter={() => onSectionHover(sectionId)}
            onMouseLeave={() => onSectionHover(null)}
            style={{ cursor: 'pointer' }}
            tabIndex={0}
            role="button"
            aria-label={`Section ${sectionId}, ${section.known_ticket_count || 0} known tickets${isStageAffected ? ', stage-affected area' : ''}`}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSectionClick(sectionId);
              }
            }}
          >
            {/* Section fill */}
            <path
              d={path}
              fill={isHovered ? COLORS.sectionHover : 
                    hasTickets ? COLORS.sectionHasTickets : 
                    'transparent'}
              stroke={hasTickets ? COLORS.seatKnown : COLORS.arenaOutline}
              strokeWidth={isHovered ? 3 : hasTickets ? 2 : 1}
              style={{ transition: 'all 0.2s' }}
            />
            
            {/* Stage-affected overlay */}
            {isStageAffected && showStage && (
              <path
                d={path}
                fill={COLORS.stageAffectedHatch}
                stroke="none"
                pointerEvents="none"
              />
            )}
            
            {/* Section label */}
            <text
              x={geo.labelX}
              y={geo.labelY}
              textAnchor="middle"
              fill={isStageAffected ? '#D4837A' : hasTickets ? '#C9A962' : '#888'}
              fontSize="24"
              fontWeight="700"
              fontFamily="'Source Sans 3', sans-serif"
              style={{ cursor: 'pointer', pointerEvents: 'none' }}
            >
              {sectionId}
            </text>
            
            {/* Ticket count badge */}
            {hasTickets && (
              <g transform={`translate(${geo.labelX + 25}, ${geo.labelY - 20})`}>
                <circle r="16" fill={COLORS.seatKnown} />
                <text
                  textAnchor="middle"
                  dy="5"
                  fill="white"
                  fontSize="12"
                  fontWeight="700"
                  fontFamily="'Source Sans 3', sans-serif"
                >
                  {section.known_ticket_count}
                </text>
              </g>
            )}
          </g>
        );
      })}
      
      {/* Stage overlay (Aloha reconstruction only) */}
      {showStage && (
        <g data-testid="stage-overlay" opacity="0.9">
          {/* Main stage */}
          <path
            d={stagePaths.mainStage}
            fill={COLORS.stageArea}
            stroke="#555"
            strokeWidth="2"
          />
          
          {/* Runway */}
          <path
            d={stagePaths.runway}
            fill={COLORS.runway}
            stroke="#555"
            strokeWidth="2"
          />
          
          {/* Stage label */}
          <text
            x={centerX}
            y={centerY - 20}
            textAnchor="middle"
            fill="#aaa"
            fontSize="18"
            fontWeight="600"
            fontFamily="'Source Sans 3', sans-serif"
          >
            STAGE
          </text>
          <text
            x={centerX}
            y={centerY + 5}
            textAnchor="middle"
            fill="#888"
            fontSize="11"
            fontFamily="'Source Sans 3', sans-serif"
          >
            Custom Television Stage
          </text>
          <text
            x={centerX}
            y={centerY + 20}
            textAnchor="middle"
            fill="#666"
            fontSize="10"
            fontFamily="'Source Sans 3', sans-serif"
          >
            with runway and mirrored structures
          </text>
          
          {/* Runway label */}
          <text
            x={centerX}
            y={centerY + 120}
            textAnchor="middle"
            fill="#777"
            fontSize="10"
            fontFamily="'Source Sans 3', sans-serif"
          >
            RUNWAY
          </text>
        </g>
      )}
    </g>
  );
};

const SectionView = ({ 
  sectionId, 
  data, 
  loading, 
  onBack, 
  onSeatClick, 
  onSeatHover,
  hoveredSeat 
}) => {
  const tickets = data?.tickets || [];
  const rows = data?.rows || [];
  const geo = SECTIONS[sectionId];
  const isStageAffected = data?.is_stage_affected;
  
  // Generate seat positions - moved before any early returns
  const seats = useMemo(() => {
    if (!geo) return [];
    const result = [];
    tickets.forEach((ticket, i) => {
      if (!ticket.row || !ticket.seat) return;
      
      const rowNum = parseInt(ticket.row) || i;
      const seatNum = parseInt(ticket.seat) || 1;
      
      // Position seats in a curved arrangement
      const baseAngle = geo.startAngle + (geo.endAngle - geo.startAngle) * 0.5;
      const rowOffset = rowNum * 15;
      const seatOffset = (seatNum - 5) * 0.08;
      
      const r = geo.innerRadius + rowOffset + 100;
      const angle = baseAngle + seatOffset;
      
      result.push({
        id: ticket.id,
        x: 600 + Math.cos(angle) * r,
        y: 450 + Math.sin(angle) * r,
        row: ticket.row,
        seat: ticket.seat,
        ticket
      });
    });
    return result;
  }, [tickets, geo]);
  
  if (loading) {
    return (
      <g>
        <text x="600" y="450" textAnchor="middle" fill="#888">
          Loading section {sectionId}...
        </text>
      </g>
    );
  }
  
  if (!data) return null;
  
  return (
    <g data-testid={`section-view-${sectionId}`}>
      {/* Section header */}
      <rect x="100" y="40" width="1000" height="80" rx="8" fill="#f8f8f8" />
      <text x="150" y="85" fill="#1a1a1a" fontSize="28" fontWeight="600"
            fontFamily="'Cormorant Garamond', serif">
        Section {sectionId}
      </text>
      <text x="150" y="105" fill="#888" fontSize="14"
            fontFamily="'Source Sans 3', sans-serif">
        {tickets.length} documented tickets • {rows.length} rows with records
        {isStageAffected && ' • Stage-affected area'}
      </text>
      
      {/* Back button */}
      <g 
        onClick={onBack} 
        style={{ cursor: 'pointer' }}
        data-testid="back-to-arena"
        tabIndex={0}
        role="button"
        aria-label="Return to full arena view"
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onBack();
          }
        }}
      >
        <rect x="950" y="55" width="120" height="40" rx="6" fill="#1a1a1a" />
        <text x="1010" y="82" textAnchor="middle" fill="white" fontSize="14"
              fontFamily="'Source Sans 3', sans-serif">
          ← Full Arena
        </text>
      </g>
      
      {/* Stage-affected notice */}
      {isStageAffected && (
        <g>
          <rect x="100" y="130" width="1000" height="60" rx="6" 
                fill="rgba(212, 131, 122, 0.1)" stroke="rgba(212, 131, 122, 0.3)" />
          <text x="130" y="160" fill="#D4837A" fontSize="13" fontWeight="600"
                fontFamily="'Source Sans 3', sans-serif">
            Stage-Affected Area
          </text>
          <text x="130" y="178" fill="#c4736a" fontSize="12"
                fontFamily="'Source Sans 3', sans-serif">
            Under the research reconstruction, some seats in this section may have been affected by the custom Aloha production stage.
          </text>
        </g>
      )}
      
      {/* Seat visualization area */}
      <rect x="100" y={isStageAffected ? 210 : 140} width="1000" height="620" 
            rx="8" fill="#fafafa" stroke="#e0e0e0" />
      
      {/* Row labels */}
      {rows.map((row, i) => (
        <text 
          key={row.row}
          x="120"
          y={isStageAffected ? 260 + i * 50 : 190 + i * 50}
          fill="#888"
          fontSize="12"
          fontFamily="'Source Sans 3', sans-serif"
        >
          Row {row.row}
        </text>
      ))}
      
      {/* Individual seats */}
      {seats.map(seat => {
        const isHovered = hoveredSeat === seat.id;
        const isGraceland = seat.ticket?.graceland_exhibition_status === 'selected_for_display';
        
        return (
          <g
            key={seat.id}
            data-testid={`seat-${seat.row}-${seat.seat}`}
            onClick={() => onSeatClick(seat)}
            onMouseEnter={() => onSeatHover(seat.id)}
            onMouseLeave={() => onSeatHover(null)}
            style={{ cursor: 'pointer' }}
            tabIndex={0}
            role="button"
            aria-label={`Row ${seat.row}, Seat ${seat.seat}${isGraceland ? ', Graceland display selection' : ''}`}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSeatClick(seat);
              }
            }}
          >
            <circle
              cx={seat.x}
              cy={seat.y}
              r={isHovered ? 12 : 10}
              fill={COLORS.seatKnown}
              stroke={isHovered ? '#1a1a1a' : 'white'}
              strokeWidth={isHovered ? 3 : 2}
              style={{ transition: 'all 0.15s' }}
            />
            {isGraceland && (
              <circle
                cx={seat.x}
                cy={seat.y}
                r="15"
                fill="none"
                stroke={COLORS.seatGraceland50}
                strokeWidth="2"
                filter="url(#graceland-glow)"
              />
            )}
            <text
              x={seat.x}
              y={seat.y + 4}
              textAnchor="middle"
              fill="white"
              fontSize="8"
              fontWeight="600"
              pointerEvents="none"
            >
              {seat.seat}
            </text>
          </g>
        );
      })}
    </g>
  );
};

const SectionTooltip = ({ section, position }) => {
  if (!section || !position) return null;
  
  const sectionId = section.name?.replace('Section ', '') || section.id?.replace('section-', '');
  
  return (
    <div
      data-testid="section-tooltip"
      style={{
        position: 'fixed',
        left: position.x + 15,
        top: position.y - 10,
        padding: '12px 16px',
        background: 'rgba(26, 26, 26, 0.95)',
        color: 'white',
        borderRadius: '8px',
        fontSize: '13px',
        fontFamily: "'Source Sans 3', sans-serif",
        zIndex: 1000,
        pointerEvents: 'none',
        minWidth: '180px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.3)'
      }}
    >
      <div style={{ fontWeight: 700, color: '#C9A962', marginBottom: '8px', fontSize: '15px' }}>
        Section {sectionId}
      </div>
      
      <div style={{ display: 'grid', gap: '4px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ color: 'rgba(255,255,255,0.7)' }}>Known tickets:</span>
          <span style={{ fontWeight: 600 }}>{section.known_ticket_count || 0}</span>
        </div>
        {section.principal_archive_count > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: 'rgba(255,255,255,0.7)' }}>Principal archive:</span>
            <span>{section.principal_archive_count}</span>
          </div>
        )}
        {section.is_stage_affected && (
          <div style={{ color: '#D4837A', marginTop: '6px', fontSize: '11px' }}>
            ⚠ Stage-affected area
          </div>
        )}
      </div>
      
      <div style={{ 
        marginTop: '10px', 
        paddingTop: '8px', 
        borderTop: '1px solid rgba(255,255,255,0.2)',
        fontSize: '11px',
        color: 'rgba(255,255,255,0.6)'
      }}>
        Click to zoom into section
      </div>
    </div>
  );
};

const SeatTooltip = ({ seat, position }) => {
  if (!seat || !position) return null;
  
  const ticket = seat.ticket;
  
  return (
    <div
      data-testid="seat-tooltip"
      style={{
        position: 'fixed',
        left: position.x + 15,
        top: position.y - 100,
        padding: '14px 18px',
        background: 'rgba(26, 26, 26, 0.95)',
        color: 'white',
        borderRadius: '8px',
        fontSize: '13px',
        fontFamily: "'Source Sans 3', sans-serif",
        zIndex: 1000,
        pointerEvents: 'none',
        minWidth: '220px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.3)'
      }}
    >
      <div style={{ fontWeight: 700, color: '#C9A962', marginBottom: '8px' }}>
        Row {seat.row}, Seat {seat.seat}
      </div>
      
      {ticket && (
        <div style={{ display: 'grid', gap: '4px', fontSize: '12px' }}>
          <div>
            <span style={{ color: 'rgba(255,255,255,0.6)' }}>Registry: </span>
            {ticket.registry_reference}
          </div>
          <div>
            <span style={{ color: 'rgba(255,255,255,0.6)' }}>Type: </span>
            {ticket.ticket_form?.replace(/_/g, ' ')}
          </div>
          <div>
            <span style={{ color: 'rgba(255,255,255,0.6)' }}>Status: </span>
            {ticket.ownership_status?.replace(/_/g, ' ')}
          </div>
          {ticket.certification_number && (
            <div>
              <span style={{ color: 'rgba(255,255,255,0.6)' }}>PSA: </span>
              #{ticket.certification_number}
            </div>
          )}
        </div>
      )}
      
      <div style={{ 
        marginTop: '10px', 
        paddingTop: '8px', 
        borderTop: '1px solid rgba(255,255,255,0.2)',
        fontSize: '11px',
        color: 'rgba(255,255,255,0.6)'
      }}>
        Click for full ticket record
      </div>
    </div>
  );
};

const TicketModal = ({ seat, onClose, isOwner = false, onEdit }) => {
  const [ticket, setTicket] = useState(seat?.ticket || null);
  const [verifications, setVerifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const modalRef = useRef(null);
  
  // Keyboard handler for ESC
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);
  
  // Focus trap
  useEffect(() => {
    if (modalRef.current) {
      modalRef.current.focus();
    }
  }, []);
  
  useEffect(() => {
    if (!seat?.ticket?.id) return;
    
    const fetchDetails = async () => {
      setLoading(true);
      try {
        // Fetch full ticket data
        const ticketRes = await fetch(`${API_BASE}/api/seat-map/tickets/${seat.ticket.id}`);
        const ticketData = await ticketRes.json();
        setTicket(ticketData);
        
        // Fetch verifications
        const verRes = await fetch(`${API_BASE}/api/seat-map/tickets/${seat.ticket.id}/verifications`);
        const verData = await verRes.json();
        setVerifications(verData.verifications || []);
      } catch (err) {
        console.error('Failed to fetch ticket details:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [seat?.ticket?.id]);
  
  if (!seat) return null;
  
  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'images', label: 'Images' },
    { id: 'provenance', label: 'Provenance' },
    { id: 'verification', label: 'Verification' },
    { id: 'sales', label: 'Sales' },
    { id: 'graceland', label: 'Graceland' },
  ];
  
  return (
    <div
      data-testid="ticket-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ticket-modal-title"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
        padding: '24px'
      }}
      onClick={onClose}
    >
      <div
        ref={modalRef}
        tabIndex={-1}
        style={{
          background: 'white',
          borderRadius: '16px',
          maxWidth: '800px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          outline: 'none'
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '24px 28px',
          borderBottom: '1px solid #eee',
          background: '#fafafa',
          borderRadius: '16px 16px 0 0'
        }}>
          <div style={{ 
            fontFamily: "'Source Sans 3', sans-serif",
            fontSize: '11px',
            color: '#C9A962',
            fontWeight: 700,
            letterSpacing: '0.1em',
            marginBottom: '8px'
          }}>
            TICKET RECORD
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{
              margin: 0,
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: '28px',
              fontWeight: 500
            }}>
              Section {ticket?.section}, Row {seat.row}, Seat {seat.seat}
            </h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {isOwner && onEdit && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit();
                  }}
                  data-testid="quick-edit-btn"
                  style={{
                    padding: '8px 16px',
                    background: '#C9A962',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontFamily: "'Source Sans 3', sans-serif"
                  }}
                >
                  Quick Edit
                </button>
              )}
              <button
                onClick={onClose}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '28px',
                  cursor: 'pointer',
                  color: '#888',
                  padding: '4px 8px'
                }}
                aria-label="Close"
              >
                ×
              </button>
            </div>
          </div>
        </div>
        
        {/* Content */}
        <div style={{ padding: '24px 28px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
              Loading ticket details...
            </div>
          ) : ticket ? (
            <div style={{ display: 'grid', gap: '24px' }}>
              {/* Identification */}
              <section>
                <h3 style={{
                  margin: '0 0 12px',
                  fontSize: '14px',
                  fontWeight: 700,
                  color: '#888',
                  letterSpacing: '0.05em',
                  fontFamily: "'Source Sans 3', sans-serif"
                }}>
                  IDENTIFICATION
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <InfoField label="Registry Reference" value={ticket.registry_reference} />
                  <InfoField label="Printed Ticket Zone" value={ticket.printed_ticket_zone?.replace(/_/g, ' ')} />
                  <InfoField label="Ticket Form" value={ticket.ticket_form?.replace(/_/g, ' ')} />
                  <InfoField label="Printed Price" value={ticket.printed_price} />
                </div>
              </section>
              
              {/* Status */}
              <section>
                <h3 style={{
                  margin: '0 0 12px',
                  fontSize: '14px',
                  fontWeight: 700,
                  color: '#888',
                  letterSpacing: '0.05em',
                  fontFamily: "'Source Sans 3', sans-serif"
                }}>
                  STATUS
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <InfoField label="Collection Relationship" value={ticket.collection_relationship?.replace(/_/g, ' ')} />
                  <InfoField label="Ownership" value={ticket.ownership_status?.replace(/_/g, ' ')} />
                  <InfoField label="Issue Status" value={ticket.issue_status?.replace(/_/g, ' ')} />
                  <InfoField label="Use Status" value={ticket.use_status?.replace(/_/g, ' ')} />
                  <InfoField label="Concert Availability" value={ticket.concert_availability?.replace(/_/g, ' ')} />
                  <InfoField label="Evidence Confidence" value={ticket.evidence_confidence?.replace(/_/g, ' ')} />
                </div>
              </section>
              
              {/* Graceland */}
              <section>
                <h3 style={{
                  margin: '0 0 12px',
                  fontSize: '14px',
                  fontWeight: 700,
                  color: '#888',
                  letterSpacing: '0.05em',
                  fontFamily: "'Source Sans 3', sans-serif"
                }}>
                  GRACELAND HISTORY
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <InfoField label="Custody Status" value={ticket.graceland_custody_status?.replace(/_/g, ' ')} />
                  <InfoField label="Exhibition Status" value={ticket.graceland_exhibition_status?.replace(/_/g, ' ')} />
                  {ticket.graceland_custody_start && (
                    <>
                      <InfoField label="Custody Start" value={ticket.graceland_custody_start} />
                      <InfoField label="Custody End" value={ticket.graceland_custody_end} />
                    </>
                  )}
                </div>
              </section>
              
              {/* Certification */}
              {ticket.certification_number && (
                <section>
                  <h3 style={{
                    margin: '0 0 12px',
                    fontSize: '14px',
                    fontWeight: 700,
                    color: '#888',
                    letterSpacing: '0.05em',
                    fontFamily: "'Source Sans 3', sans-serif"
                  }}>
                    CERTIFICATION
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <InfoField label="Company" value={ticket.certification_company || 'PSA'} />
                    <InfoField label="Number" value={`#${ticket.certification_number}`} />
                    <InfoField label="Grade" value={ticket.grade} />
                    <InfoField label="Designation" value={ticket.psa_designation} />
                  </div>
                </section>
              )}
              
              {/* Verification Badges */}
              <section>
                <h3 style={{
                  margin: '0 0 12px',
                  fontSize: '14px',
                  fontWeight: 700,
                  color: '#888',
                  letterSpacing: '0.05em',
                  fontFamily: "'Source Sans 3', sans-serif"
                }}>
                  VERIFICATION
                </h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {ticket.verification_visual_review && (
                    <VerificationBadge label="Visual Review Passed" />
                  )}
                  {ticket.verification_physical_inspection && (
                    <VerificationBadge label="Expert Physical Inspection" />
                  )}
                  {ticket.verification_psa_graded && (
                    <VerificationBadge label="PSA Graded" />
                  )}
                  {ticket.verification_other_grading && (
                    <VerificationBadge label="Third-Party Certified" />
                  )}
                  {ticket.verification_provenance_documented && (
                    <VerificationBadge label="Provenance Documented" />
                  )}
                  {ticket.verification_displayed_graceland && (
                    <VerificationBadge label="Displayed at Graceland" special />
                  )}
                  {!ticket.verification_visual_review && !ticket.verification_psa_graded && (
                    <span style={{
                      padding: '6px 12px',
                      background: '#f5f5f5',
                      borderRadius: '4px',
                      fontSize: '12px',
                      color: '#888'
                    }}>
                      Verification pending
                    </span>
                  )}
                </div>
              </section>
              
              {/* Provenance */}
              {ticket.provenance && (
                <section>
                  <h3 style={{
                    margin: '0 0 12px',
                    fontSize: '14px',
                    fontWeight: 700,
                    color: '#888',
                    letterSpacing: '0.05em',
                    fontFamily: "'Source Sans 3', sans-serif"
                  }}>
                    PROVENANCE
                  </h3>
                  <p style={{
                    margin: 0,
                    fontSize: '14px',
                    lineHeight: 1.6,
                    color: '#333',
                    fontFamily: "'Source Sans 3', sans-serif"
                  }}>
                    {ticket.provenance}
                  </p>
                </section>
              )}
              
              {/* Research Notes */}
              {ticket.research_notes_public && (
                <section>
                  <h3 style={{
                    margin: '0 0 12px',
                    fontSize: '14px',
                    fontWeight: 700,
                    color: '#888',
                    letterSpacing: '0.05em',
                    fontFamily: "'Source Sans 3', sans-serif"
                  }}>
                    RESEARCH NOTES
                  </h3>
                  <p style={{
                    margin: 0,
                    fontSize: '14px',
                    lineHeight: 1.6,
                    color: '#333',
                    fontFamily: "'Source Sans 3', sans-serif"
                  }}>
                    {ticket.research_notes_public}
                  </p>
                </section>
              )}
              
              {/* Open Questions */}
              {ticket.open_research_questions && (
                <section style={{
                  padding: '16px',
                  background: '#fff8e6',
                  border: '1px solid #e6d9a8',
                  borderRadius: '8px'
                }}>
                  <h3 style={{
                    margin: '0 0 8px',
                    fontSize: '13px',
                    fontWeight: 700,
                    color: '#7a6832',
                    fontFamily: "'Source Sans 3', sans-serif"
                  }}>
                    Open Research Questions
                  </h3>
                  <p style={{
                    margin: 0,
                    fontSize: '13px',
                    lineHeight: 1.5,
                    color: '#8a7942',
                    fontFamily: "'Source Sans 3', sans-serif"
                  }}>
                    {ticket.open_research_questions}
                  </p>
                </section>
              )}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
              No ticket record found
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const InfoField = ({ label, value }) => (
  <div>
    <div style={{
      fontSize: '11px',
      color: '#888',
      fontWeight: 600,
      marginBottom: '4px',
      fontFamily: "'Source Sans 3', sans-serif"
    }}>
      {label}
    </div>
    <div style={{
      fontSize: '14px',
      color: value ? '#1a1a1a' : '#ccc',
      fontFamily: "'Source Sans 3', sans-serif",
      textTransform: 'capitalize'
    }}>
      {value || '—'}
    </div>
  </div>
);

const VerificationBadge = ({ label, special }) => (
  <span style={{
    padding: '6px 12px',
    background: special ? 'rgba(255, 215, 0, 0.15)' : 'rgba(201, 169, 98, 0.15)',
    border: special ? '1px solid rgba(255, 215, 0, 0.3)' : '1px solid rgba(201, 169, 98, 0.3)',
    borderRadius: '4px',
    fontSize: '12px',
    color: special ? '#b8860b' : '#9a7b32',
    fontWeight: 600,
    fontFamily: "'Source Sans 3', sans-serif"
  }}>
    ✓ {label}
  </span>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const ArenaExplorer = ({ onSeatSelect, isOwnerMode = false }) => {
  const { sections, loading: sectionsLoading } = useArenaSections();
  const stats = useInventoryStats();
  
  const [viewLevel, setViewLevel] = useState('arena'); // arena, section, seat
  const [selectedSection, setSelectedSection] = useState(null);
  const [layoutView, setLayoutView] = useState('aloha-1973');
  const [dataLayer, setDataLayer] = useState('all');
  
  const [hoveredSection, setHoveredSection] = useState(null);
  const [hoveredSeat, setHoveredSeat] = useState(null);
  const [sectionTooltipPos, setSectionTooltipPos] = useState(null);
  const [seatTooltipPos, setSeatTooltipPos] = useState(null);
  
  const [selectedSeat, setSelectedSeat] = useState(null);
  const [isOwner, setIsOwner] = useState(false);
  const [showQuickEdit, setShowQuickEdit] = useState(false);
  const [editingSeat, setEditingSeat] = useState(null);
  
  // Check owner authentication
  useEffect(() => {
    const checkOwner = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/seat-map/auth/me`, {
          credentials: 'include'
        });
        if (res.ok) {
          const data = await res.json();
          setIsOwner(data.role === 'owner_admin');
        }
      } catch {
        setIsOwner(false);
      }
    };
    checkOwner();
  }, []);
  
  const { data: sectionData, loading: sectionLoading } = useSectionTickets(
    viewLevel === 'section' ? selectedSection : null
  );
  
  const transformRef = useRef(null);
  
  const handleSectionClick = useCallback((sectionId) => {
    setSelectedSection(sectionId);
    setViewLevel('section');
    setHoveredSection(null);
    // Reset zoom
    if (transformRef.current) {
      transformRef.current.resetTransform();
    }
  }, []);
  
  const handleSectionHover = useCallback((sectionId, e) => {
    setHoveredSection(sectionId);
    if (sectionId && e) {
      setSectionTooltipPos({ x: e.clientX, y: e.clientY });
    } else {
      setSectionTooltipPos(null);
    }
  }, []);
  
  const handleSeatClick = useCallback((seat) => {
    setSelectedSeat(seat);
    if (onSeatSelect) {
      onSeatSelect(seat, seat.ticket);
    }
  }, [onSeatSelect]);
  
  const handleQuickEdit = useCallback((seat) => {
    setEditingSeat(seat);
    setShowQuickEdit(true);
  }, []);
  
  const handleQuickEditClose = useCallback(() => {
    setShowQuickEdit(false);
    setEditingSeat(null);
  }, []);
  
  const handleQuickEditSave = useCallback((updatedTicket) => {
    // Refresh section data after edit
    // The ticket is already updated via the API response
    console.log('Ticket updated:', updatedTicket);
  }, []);
  
  const handleSeatHover = useCallback((seatId, e) => {
    setHoveredSeat(seatId);
    if (seatId && e) {
      setSeatTooltipPos({ x: e.clientX, y: e.clientY });
    } else {
      setSeatTooltipPos(null);
    }
  }, []);
  
  const handleBackToArena = useCallback(() => {
    setViewLevel('arena');
    setSelectedSection(null);
    setHoveredSeat(null);
  }, []);
  
  const hoveredSectionData = useMemo(() => {
    if (!hoveredSection) return null;
    return sections.find(s => 
      s.name?.replace('Section ', '') === hoveredSection ||
      s.id?.replace('section-', '') === hoveredSection
    );
  }, [hoveredSection, sections]);
  
  const hoveredSeatData = useMemo(() => {
    if (!hoveredSeat || !sectionData?.tickets) return null;
    const ticket = sectionData.tickets.find(t => t.id === hoveredSeat);
    if (!ticket) return null;
    return { id: hoveredSeat, row: ticket.row, seat: ticket.seat, ticket };
  }, [hoveredSeat, sectionData]);
  
  if (sectionsLoading) {
    return (
      <div data-testid="arena-loading" style={{ 
        padding: '80px', 
        textAlign: 'center',
        fontFamily: "'Source Sans 3', sans-serif",
        color: '#888'
      }}>
        Loading arena data...
      </div>
    );
  }
  
  return (
    <div data-testid="arena-explorer" style={{ width: '100%' }}>
      {/* Controls */}
      <div style={{ 
        display: 'flex', 
        flexWrap: 'wrap', 
        gap: '16px', 
        alignItems: 'center',
        marginBottom: '20px',
        padding: '16px 20px',
        background: '#f8f8f8',
        borderRadius: '8px'
      }}>
        {/* Layout selector */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {LAYOUT_VIEWS.map(view => (
            <button
              key={view.id}
              data-testid={`layout-${view.id}`}
              onClick={() => setLayoutView(view.id)}
              style={{
                padding: '10px 16px',
                minHeight: '44px',
                background: layoutView === view.id ? '#1a1a1a' : 'white',
                color: layoutView === view.id ? 'white' : '#666',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: layoutView === view.id ? 600 : 400,
                fontFamily: "'Source Sans 3', sans-serif",
                cursor: 'pointer',
                transition: 'all 0.15s'
              }}
            >
              {view.name}
            </button>
          ))}
        </div>
        
        {/* Section shortcuts */}
        <div style={{ 
          display: 'flex', 
          gap: '8px', 
          marginLeft: 'auto',
          alignItems: 'center'
        }}>
          <span style={{ 
            fontSize: '12px', 
            color: '#888',
            fontFamily: "'Source Sans 3', sans-serif"
          }}>
            Jump to:
          </span>
          {['AA', 'BB', 'H', 'L'].map(sectionId => (
            <button
              key={sectionId}
              data-testid={`jump-${sectionId}`}
              onClick={() => handleSectionClick(sectionId)}
              style={{
                width: '40px',
                height: '40px',
                background: SECTIONS[sectionId]?.isStageAffected 
                  ? 'rgba(212, 131, 122, 0.1)' 
                  : 'rgba(201, 169, 98, 0.1)',
                border: SECTIONS[sectionId]?.isStageAffected
                  ? '1px solid rgba(212, 131, 122, 0.3)'
                  : '1px solid rgba(201, 169, 98, 0.3)',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: 700,
                color: SECTIONS[sectionId]?.isStageAffected ? '#D4837A' : '#C9A962',
                fontFamily: "'Source Sans 3', sans-serif",
                cursor: 'pointer'
              }}
            >
              {sectionId}
            </button>
          ))}
        </div>
      </div>
      
      {/* Research notice */}
      {layoutView === 'aloha-1973' && viewLevel === 'arena' && (
        <div style={{
          padding: '12px 16px',
          background: 'rgba(201, 169, 98, 0.08)',
          border: '1px solid rgba(201, 169, 98, 0.2)',
          borderRadius: '6px',
          marginBottom: '16px'
        }}>
          <strong style={{ color: '#7a6832' }}>Research Reconstruction:</strong>
          <span style={{ color: '#8a7942', marginLeft: '8px' }}>
            This layout represents the researched January 14, 1973 concert configuration based on photographs, 
            production accounts, surviving tickets, and venue documentation. 
            It is not the verified original seating chart.
          </span>
        </div>
      )}
      
      {layoutView !== 'aloha-1973' && viewLevel === 'arena' && (
        <div style={{
          padding: '12px 16px',
          background: '#f5f5f5',
          border: '1px solid #e0e0e0',
          borderRadius: '6px',
          marginBottom: '16px'
        }}>
          <strong style={{ color: '#666' }}>Venue Reference:</strong>
          <span style={{ color: '#888', marginLeft: '8px' }}>
            This is a later Blaisdell Center reference configuration. 
            It illustrates how the arena can be rearranged but does not document the exact January 1973 Aloha production.
          </span>
        </div>
      )}
      
      {/* Map container */}
      <div style={{
        background: '#fafafa',
        borderRadius: '12px',
        border: '1px solid #e0e0e0',
        overflow: 'hidden',
        minHeight: '600px'
      }}>
        <TransformWrapper
          ref={transformRef}
          initialScale={1}
          minScale={0.5}
          maxScale={3}
          centerOnInit
          wheel={{ step: 0.1 }}
        >
          {({ zoomIn, zoomOut, resetTransform }) => (
            <>
              {/* Zoom controls */}
              <div style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                zIndex: 10
              }}>
                <button
                  onClick={() => zoomIn()}
                  style={{
                    width: '40px',
                    height: '40px',
                    background: 'white',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '20px',
                    cursor: 'pointer'
                  }}
                  aria-label="Zoom in"
                >
                  +
                </button>
                <button
                  onClick={() => zoomOut()}
                  style={{
                    width: '40px',
                    height: '40px',
                    background: 'white',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '20px',
                    cursor: 'pointer'
                  }}
                  aria-label="Zoom out"
                >
                  −
                </button>
                <button
                  onClick={() => resetTransform()}
                  style={{
                    width: '40px',
                    height: '40px',
                    background: 'white',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '12px',
                    cursor: 'pointer',
                    fontFamily: "'Source Sans 3', sans-serif"
                  }}
                  aria-label="Reset view"
                >
                  ↺
                </button>
              </div>
              
              <TransformComponent>
                <svg
                  data-testid="arena-svg"
                  viewBox="0 0 1200 900"
                  width="100%"
                  style={{ minHeight: '600px' }}
                  role="img"
                  aria-label="Interactive Neal S. Blaisdell Arena seating chart"
                >
                  {viewLevel === 'arena' ? (
                    <ArenaOverview
                      sections={sections}
                      layoutView={layoutView}
                      onSectionClick={handleSectionClick}
                      onSectionHover={(id) => {
                        // Get mouse position from event
                        const handleMove = (e) => {
                          handleSectionHover(id, e);
                          window.removeEventListener('mousemove', handleMove);
                        };
                        if (id) {
                          window.addEventListener('mousemove', handleMove, { once: true });
                        } else {
                          handleSectionHover(null);
                        }
                      }}
                      hoveredSection={hoveredSection}
                    />
                  ) : (
                    <SectionView
                      sectionId={selectedSection}
                      data={sectionData}
                      loading={sectionLoading}
                      onBack={handleBackToArena}
                      onSeatClick={handleSeatClick}
                      onSeatHover={(id) => {
                        const handleMove = (e) => {
                          handleSeatHover(id, e);
                          window.removeEventListener('mousemove', handleMove);
                        };
                        if (id) {
                          window.addEventListener('mousemove', handleMove, { once: true });
                        } else {
                          handleSeatHover(null);
                        }
                      }}
                      hoveredSeat={hoveredSeat}
                    />
                  )}
                </svg>
              </TransformComponent>
            </>
          )}
        </TransformWrapper>
      </div>
      
      {/* Stats summary */}
      {stats && viewLevel === 'arena' && (
        <div style={{
          display: 'flex',
          gap: '32px',
          padding: '24px 0',
          marginTop: '16px',
          borderTop: '1px solid #eee',
          flexWrap: 'wrap'
        }}>
          <StatBox label="Original Inventory" value={stats.total_tickets || 0} subtitle="June 21, 2021" />
          <StatBox label="Seat-Numbered" value={stats.seat_numbered || 0} color="#C9A962" />
          <StatBox label="Blank Tickets" value={stats.blank || 0} color="#888" />
          <StatBox label="Graceland Custody" value={stats.graceland_custody_count || 0} color="#5B7BA3" />
          <StatBox label="Display Selection" value={`${stats.graceland_display_count || 0}/50`} color="#FFD700" subtitle="Identification pending" />
        </div>
      )}
      
      {/* Tooltips */}
      {hoveredSectionData && sectionTooltipPos && (
        <SectionTooltip section={hoveredSectionData} position={sectionTooltipPos} />
      )}
      
      {hoveredSeatData && seatTooltipPos && (
        <SeatTooltip seat={hoveredSeatData} position={seatTooltipPos} />
      )}
      
      {/* Ticket Modal */}
      {selectedSeat && (
        <TicketModal 
          seat={selectedSeat} 
          onClose={() => setSelectedSeat(null)}
          isOwner={isOwner}
          onEdit={isOwner ? () => handleQuickEdit(selectedSeat) : undefined}
        />
      )}
      
      {/* Quick Edit Panel for Owners */}
      {showQuickEdit && editingSeat?.ticket && (
        <QuickEditPanel
          ticket={editingSeat.ticket}
          onClose={handleQuickEditClose}
          onSave={handleQuickEditSave}
          onOpenFullRecord={() => {
            window.location.href = `/owner-dashboard/tickets?id=${editingSeat.ticket.id}`;
          }}
        />
      )}
    </div>
  );
};

const StatBox = ({ label, value, color = '#1a1a1a', subtitle }) => (
  <div>
    <div style={{ 
      fontFamily: "'Cormorant Garamond', serif", 
      fontSize: '32px', 
      fontWeight: 500, 
      color 
    }}>
      {value}
    </div>
    <div style={{ 
      fontFamily: "'Source Sans 3', sans-serif", 
      fontSize: '12px', 
      color: '#888' 
    }}>
      {label}
    </div>
    {subtitle && (
      <div style={{ 
        fontFamily: "'Source Sans 3', sans-serif", 
        fontSize: '10px', 
        color: '#aaa',
        marginTop: '2px'
      }}>
        {subtitle}
      </div>
    )}
  </div>
);

export default ArenaExplorer;
